/**
 * `useDisclosureState` is a helper function that will provide props for
 * implementing the disclosure pattern.
 */
import * as React from "react"
import useInternalId from "../useInternalId"

type InitialState = Partial<typeof defaultState>

const defaultState = {
  /**
   * A string to prepend to the controlled element id.
   * @default "disclosure"
   */
  hiddenId: "disclosure",
  /**
   * Sets whether focus should return to the disclosure.
   * @default true
   */
  returnFocus: true,
  /**
   * The default value for visible
   * @default false
   */
  visible: false
}

const activeElement = () =>
  document ? (document.activeElement as HTMLElement) : null

/**
 *
 * @param initialState
 */
const useDisclosureState = (initialState: InitialState = {}) => {
  const [{ hiddenId, returnFocus, visible: defaultVisible }] = React.useState({
    ...defaultState,
    ...initialState
  })
  const [visible, setVisible] = React.useState(defaultVisible)
  const [returnElement, setReturnElement] = React.useState<HTMLElement | null>(
    visible ? activeElement() : null
  )

  // set visibility if prop changes
  React.useEffect(() => {
    setVisible(defaultVisible)
  }, [defaultVisible])

  const show = React.useCallback((event?: React.MouseEvent) => {
    event?.preventDefault()
    event?.stopPropagation()
    setVisible(true)
  }, [])
  const hide = React.useCallback(
    (event?: React.MouseEvent) => {
      event?.preventDefault()
      event?.stopPropagation()
      returnFocus && returnElement?.focus({ preventScroll: true })
      setVisible(false)
    },
    [returnElement, returnFocus]
  )
  const toggle = React.useCallback(
    (event?: React.MouseEvent) => (visible ? hide(event) : show(event)),
    [hide, show, visible]
  )
  const handleClick = React.useCallback<React.MouseEventHandler<HTMLElement>>(
    event => {
      event.preventDefault()
      event.stopPropagation()
      setReturnElement(event.currentTarget)
      toggle()
    },
    [toggle]
  )

  const id = useInternalId(hiddenId)

  return {
    bind: {
      id
    },
    bindDisclosure: {
      "aria-controls": visible ? id : undefined,
      "aria-expanded": visible,
      onClick: handleClick,
      role: "button" as "button",
      type: "button" as "button"
    },
    hide,
    setReturnElement,
    show,
    toggle,
    visible
  }
}

export default useDisclosureState
